/* eslint-disable camelcase */
import { Directionality } from '@angular/cdk/bidi';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT, effect, inject, Injector, RendererFactory2, Service } from '@angular/core';
import { BehaviorSubject, filter, first, map, take } from 'rxjs';

import { ThemesService } from '@ideals/theming';
import { getCssColor } from '@ideals/utils/get-css-var';

declare global {
  interface Window {
    // eslint-disable-next-line custom-rules/prefer-interface-property-readonly
    Intercom: Intercom;
    // eslint-disable-next-line custom-rules/prefer-interface-property-readonly
    intercomSettings: IntercomSettings;
  }
}

interface IntercomSettingsDto {
  readonly action_color: string;
  readonly app_id: string;
  readonly background_color: string;
  readonly enabled: boolean;
  readonly ip: string;
  readonly session_duration: number;
  readonly user_jwt?: string;
}

interface IntercomSettings {
  readonly account_manager?: string | null;
  readonly action_color?: string;
  readonly alignment?: 'left' | 'right';
  readonly app_id?: string;
  readonly apply_permission_count_admin?: number;
  readonly background_color?: string;
  readonly business_development_manager?: string | null;
  readonly contract_number?: string | null;
  readonly enabled?: boolean;
  readonly excel_view_count?: number;
  readonly Full_CA_ID?: string;
  readonly hide_default_launcher?: boolean;
  readonly horizontal_padding?: number;
  readonly intercom_user_jwt?: string;
  readonly ip?: string;
  readonly last_survey_date_at?: number;
  readonly new_qna_approve_count?: number;
  readonly new_qna_message_count?: number;
  readonly number_of_move_copy_restore_operations?: number;
  readonly PDF_view_count?: number;
  readonly product_type?: string;
  readonly qna_version?: string;
  readonly redact_count?: number;
  readonly report_export_count_admin?: number;
  readonly report_view_count_admin?: number;
  readonly save_draft_count?: number;
  readonly session_duration?: number;
  readonly subscriptionPlan?: string;
  readonly theme_mode?: 'dark' | 'light';
}

interface IntercomCommandSignature {
  readonly hide: () => void;
  readonly onHide: (callback: () => void) => void;
  readonly onShow: (callback: () => void) => void;
  readonly show: () => void;
  readonly shutdown: () => void;
  readonly update: (intercomSettings?: IntercomSettings) => void;
}

type IntercomCommand = keyof IntercomCommandSignature;

type Intercom = <Command extends IntercomCommand>(
  command: Command,
  ...params: Parameters<IntercomCommandSignature[Command]>
) => ReturnType<IntercomCommandSignature[Command]>;

export const DEFAULT_HORIZONTAL_PADDING = 20;

@Service()
export class IntercomService {
  readonly #directionality = inject(Directionality);
  readonly #document = inject(DOCUMENT);
  readonly #httpClient = inject(HttpClient);
  readonly #injector = inject(Injector);
  readonly #intercomLoaded = new BehaviorSubject<boolean>(false);
  readonly #renderer2 = inject(RendererFactory2).createRenderer(null, null);
  readonly #themesService = inject(ThemesService);

  get intercom(): Intercom | undefined {
    return window.Intercom;
  }

  initialize(): void {
    this.#httpClient.get<IntercomSettingsDto>('/api/ui-settings/intercom')
      .pipe(
        filter(({ enabled }) => !!enabled),
        map((settings) => {
          const { user_jwt, ...intercomSettings } = settings;

          return {
            ...intercomSettings,
            horizontal_padding: DEFAULT_HORIZONTAL_PADDING,
            product_type: 'VDR',
            ...user_jwt && { intercom_user_jwt: user_jwt },
          };
        }),
        take(1),
      )
      .subscribe((intercomSettings) => {
        this.#createIntercomScript(intercomSettings);
      });
  }

  setHorizontalPadding(padding: number): void {
    this.updateIntercom({ horizontal_padding: padding });
  }

  showIntercomChat(): void {
    this.intercom?.('show');
  }

  shutdown(): void {
    this.intercom?.('shutdown');
  }

  updateIntercom(intercomSettings: IntercomSettings = {}): void {
    this.#intercomLoaded
      .pipe(
        filter((loaded) => loaded),
        first(),
      )
      .subscribe(() => {
        this.#updateIntercomSettings(intercomSettings);
        this.intercom?.('update', intercomSettings);
      });
  }

  #createIntercomScript(intercomSettings: IntercomSettings): void {
    const script = this.#renderer2.createElement('script') as HTMLScriptElement;
    const scriptSrc = `https://widget.intercom.io/widget/${intercomSettings.app_id!}`;
    const alignment = this.#directionality.value === 'rtl' ? 'left' : 'right';

    this.#updateIntercomSettings({ ...intercomSettings, alignment });
    this.#renderer2.setAttribute(script, 'async', '');
    this.#renderer2.setAttribute(script, 'src', scriptSrc);
    this.#renderer2.appendChild(this.#document.head, script);
    this.#updateIntercomSurveyDate();

    script.onload = (): void => {
      this.#intercomLoaded.next(true);
      this.#updateIntercomStyles();
      this.#subscribeToColorChange();
      this.#subscribeToThemeModeChange();
      this.#subscribeToDirectionChange();
    };
  }

  #subscribeToColorChange(): void {
    effect(() => {
      this.#themesService.themeName();

      const color = getCssColor('ideals-primary-500');

      this.#updateIntercomSettings({
        action_color: color,
        background_color: color,
      });
      this.intercom?.('update');
    }, { injector: this.#injector });
  }

  #subscribeToDirectionChange(): void {
    this.#directionality.change
      .subscribe((direction) => {
        const alignment = direction === 'rtl' ? 'left' : 'right';

        this.#updateIntercomSettings({ alignment });
        this.intercom?.('update');
      });
  }

  #subscribeToThemeModeChange(): void {
    let themeUpdated = false;

    effect(() => {
      this.updateIntercom({
        theme_mode: this.#themesService.mode(),
      });

      if (!themeUpdated) {
        // Intercom updates messenger theme only after first open
        this.intercom?.('show');
        this.intercom?.('hide');
        themeUpdated = true;
      }

      const messengerStyles = `
        <style>
          .intercom-18kcpf8,
          .intercom-1v1jmjb,
          .intercom-fup3w2,
          .intercom-387jjl { color: ${getCssColor('ideals-text-color')} }
        </style>`;
      const launcherStyles = `
        <style>
          .intercom-1uav4b svg path,
          .intercom-dequ76 svg path,
          .intercom-o9spip svg path { fill: white }
        </style>`;

      let messengerIframe = this.#document.querySelector('.intercom-messenger-frame iframe') as HTMLIFrameElement;
      let launcherIframe = this.#document.querySelector('iframe.intercom-launcher-frame') as HTMLIFrameElement;

      if (messengerIframe && launcherIframe) {
        messengerIframe.contentDocument?.head?.insertAdjacentHTML('beforeend', messengerStyles);
        launcherIframe.contentDocument?.head?.insertAdjacentHTML('beforeend', launcherStyles);
      } else {
        const intervalTime = 100;
        let messengerIntervalId: ReturnType<typeof setTimeout> | undefined = undefined;

        this.intercom?.('onShow', (): void => {
          messengerIntervalId = setInterval(() => {
            messengerIframe = this.#document.querySelector('.intercom-messenger-frame iframe') as HTMLIFrameElement;

            if (messengerIframe) {
              messengerIframe.contentDocument?.head?.insertAdjacentHTML('beforeend', messengerStyles);
              clearInterval(messengerIntervalId);
            }
          }, intervalTime);
        });

        this.intercom?.('onHide', (): void => {
          if (messengerIntervalId) {
            clearInterval(messengerIntervalId);
          }
        });

        const launcherIntervalId = setInterval(() => {
          launcherIframe = this.#document.querySelector('iframe.intercom-launcher-frame') as HTMLIFrameElement;

          if (launcherIframe) {
            launcherIframe.contentDocument?.head?.insertAdjacentHTML('beforeend', launcherStyles);
            clearInterval(launcherIntervalId);
          }
        }, intervalTime);
      }
    }, { injector: this.#injector });
  }

  #updateIntercomSettings(intercomSettings: IntercomSettings): void {
    window.intercomSettings = { ...window.intercomSettings, ...intercomSettings };
  }

  #updateIntercomStyles(): void {
    const styles = `
      <style>
        .intercom-1o4wo1x,.e1tiptuf3 { font-size: 0; }
        .intercom-1o4wo1x:after,.e1tiptuf3:after { content: "30 sec."; font-size: 14px; }
      </style>`;
    let intervalId: ReturnType<typeof setTimeout> | undefined = undefined;

    this.intercom?.('onShow', (): void => {
      const intervalTime = 100;

      intervalId = setInterval(() => {
        const messengerIframe = this.#document.querySelector('.intercom-messenger-frame iframe') as HTMLIFrameElement;
        const messenger = messengerIframe?.contentDocument?.querySelector('.intercom-messenger') as HTMLIFrameElement;

        if (messenger) {
          messengerIframe?.contentDocument?.head?.insertAdjacentHTML('beforeend', styles);
          clearInterval(intervalId);
        }
      }, intervalTime);
    });

    this.intercom?.('onHide', (): void => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });
  }

  #updateIntercomSurveyDate(): void {
    if (window.intercomSettings?.intercom_user_jwt) {
      const milliseconds = 1000;
      const intervalTime = 1000;
      const intervalId = setInterval(() => {
        const notificationsFrame = (this.#document.querySelector('iframe[name="intercom-notifications-frame"]')
          ?? this.#document.querySelector('iframe[name="intercom-borderless-frame"]')
          ?? this.#document.querySelector('iframe[name="intercom-notification-stack-frame"]')
        ) as HTMLIFrameElement;
        const frameDocument = notificationsFrame?.contentDocument;
        const xPath
          = '//*[self::span or self::h1 or self::p or self::div]'
          + '[contains(translate(., "ABCDEFGHIJKLMNOPQRSTUVWXYZ", "abcdefghijklmnopqrstuvwxyz"), "tasha")]';
        const surveyAuthor = frameDocument?.evaluate(
          xPath,
          frameDocument,
          null,
          XPathResult.FIRST_ORDERED_NODE_TYPE,
          null,
        );

        if (surveyAuthor?.singleNodeValue) {
          this.#updateIntercomSettings({ last_survey_date_at: Math.floor(Date.now() / milliseconds) });
          this.intercom?.('update');
          clearInterval(intervalId);
        }
      }, intervalTime);
    }
  }
}
